/** The date-specific links (U4: hidden until the filter shows them — mostly guides'
 *  per-booking links, and there may be many): the console's server-driven grid
 *  (`ResourceDataGrid`), 25 rows a page, upcoming dates first (past ones muted, with a
 *  history icon and "past" in their words), searchable by a pasted link URL or any part of
 *  its UUID, and filtered by date — the TimeWindowPicker range widget plus the quick
 *  ranges **Upcoming** / **Past 30 days** / **All**. Any filter, search, sort or Show
 *  revoked change goes back to page 1. Keyboard is the grid's own; the Actions column
 *  (pinned right) holds Copy, QR and the same ⋯ menu as the tour-wide list.
 *
 *  The tour editor's panel is about 720px at 1440 (DESIGN: content − 380 − 16). Ten columns do
 *  not fit it honestly — Tags, Guide booking and Status would sit at about 56px, too narrow for
 *  a chip or a name — so Date is pinned left and Actions right, and the columns between them
 *  scroll sideways at their readable widths (`DATED_COLUMN_WIDTHS`). */
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HikingOutlinedIcon from '@mui/icons-material/HikingOutlined';
import HistoryIcon from '@mui/icons-material/History';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { GridActionsCellItem, type GridActionsCellItemProps, type GridColDef } from '@mui/x-data-grid-pro';
import { format, subDays } from 'date-fns';
import { useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as toursApi from '@/api/tours';
import type { SelfregLinkListParams, TourSelfregLink } from '@/api/tours';
import type { PaginatedResponse } from '@/api/users';
import { ResourceDataGrid, type ResourceDataGridParams } from '@/components/DataGrid/ResourceDataGrid';
import { Segmented } from '@/components/Segmented';
import { TimeWindowPicker, type TimeWindowTz } from '@/components/TimeWindowPicker';
import { formatWallDate } from '@/features/records/editor/format';

import {
  addressTail,
  createdWords,
  formatFullDate,
  formatShortDate,
  KIND_LABEL,
  REVOKED_NO_COPY,
  registeredShort,
  verificationWords,
} from './linkWords';
import { LoadError } from './LoadError';
import { DATED_PAGE_SIZE, datedKey } from './queries';
import { MONO } from './TourWideList';

export type QuickRange = 'upcoming' | 'past30' | 'all';

export interface DateRange {
  from: string | null;
  to: string | null;
}

/** Each column's width (a flex column's minimum) — the widths at which each reads whole. */
export const DATED_COLUMN_WIDTHS = {
  date: 96,
  kind: 88,
  tags: 120,
  guide_booking: 110,
  created_ts: 88,
  registered_count: 80,
  verification: 112,
  status: 108,
  link: 116,
  actions: 96,
} as const;

/** Pinned so the row's date and its actions stay in view while the middle scrolls. */
export const DATED_PINNED = { left: ['date'], right: ['actions'] } as const;

/** The class of a revoked link's row (tinted, as in the tour-wide list). */
export const REVOKED_ROW_CLASS = 'selfreg-revoked-row';

/** The panel width the grid is laid out for (DESIGN: content − 380 − 16 at 1440). */
export const DATED_GRID_FIT_PX = 720;

/** Guide / Custom (and the legacy kinds), each with its own icon beside the word. */
const KIND_ICON: Record<TourSelfregLink['kind'], typeof HikingOutlinedIcon> = {
  guide: HikingOutlinedIcon,
  custom: LinkOutlinedIcon,
  master: MailOutlineIcon,
  observer: VisibilityOutlinedIcon,
};

const today = () => format(new Date(), 'yyyy-MM-dd');

// px strings: in sx a bare `1` is 100%.
const VISUALLY_HIDDEN = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  m: '-1px',
  p: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

/** The quick ranges, as inclusive travel dates. */
export function quickRange(q: QuickRange, now: Date = new Date()): DateRange {
  const day = format(now, 'yyyy-MM-dd');
  if (q === 'upcoming') return { from: day, to: null };
  if (q === 'past30') return { from: format(subDays(now, 30), 'yyyy-MM-dd'), to: day };
  return { from: null, to: null };
}

/** A travel date as the picker's instant: midnight (or 23:59:59, the end of the day) on that
 *  date in the zone the picker shows (UTC or the browser's). */
export function dateToInstant(date: string | null, end: boolean, tz: TimeWindowTz): Date | null {
  if (!date) return null;
  const [y, m, d] = date.split('-').map(Number);
  const [h, min, sec] = end ? [23, 59, 59] : [0, 0, 0];
  return tz === 'utc' ? new Date(Date.UTC(y!, m! - 1, d!, h, min, sec)) : new Date(y!, m! - 1, d!, h, min, sec);
}

/** The picker's instant back to a travel date: the date it shows (its zone's), never the
 *  instant's UTC date in Local mode (Sydney's "10 Jan 00:00" is 9 Jan in UTC). */
export function instantToDate(d: Date | null, tz: TimeWindowTz): string | null {
  if (!d) return null;
  return tz === 'utc' ? d.toISOString().slice(0, 10) : format(d, 'yyyy-MM-dd');
}

/** "4 Oct" (the year when it is not this one) — the full date is in the tooltip. */
export function shortWallDate(date: string, nowYear = new Date().getFullYear()): string {
  const [y, m, d] = date.split('-').map(Number);
  const label = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
    ...(y === nowYear ? {} : { year: 'numeric' }),
  }).format(Date.UTC(y!, m! - 1, d!));
  return label;
}

/** One line, ellipsis, its whole words in the tooltip (describing, not naming, it). */
function Clipped({ tooltip, children, sx }: { tooltip: string; children: ReactNode; sx?: object }) {
  return (
    <Tooltip title={tooltip} describeChild>
      <Box
        component="span"
        sx={{ display: 'block', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...sx }}
      >
        {children}
      </Box>
    </Tooltip>
  );
}

/** An actions-cell button; disabled with its reason in a tooltip (the span keeps the
 *  tooltip working over a disabled button). The cell clones it with its tabIndex and click
 *  handler, so those pass through. */
function CellAction({
  reason,
  ...props
}: GridActionsCellItemProps & { showInMenu?: false; reason?: string }) {
  const item = <GridActionsCellItem {...props} />;
  return props.disabled && reason ? (
    <Tooltip title={reason}>
      <span data-disabled-reason>{item}</span>
    </Tooltip>
  ) : (
    item
  );
}

export function DatedLinksGrid({
  tourId,
  showRevoked,
  canSeeBookings,
  onCopy,
  onQr,
  onMenu,
}: {
  tourId: number;
  showRevoked: boolean;
  /** Guide bookings link to their record only with console_bookings_view. */
  canSeeBookings: boolean;
  onCopy: (link: TourSelfregLink) => void;
  onQr: (link: TourSelfregLink, anchor: HTMLElement) => void;
  onMenu: (link: TourSelfregLink, anchor: HTMLElement) => void;
}) {
  const [quick, setQuick] = useState<QuickRange | null>('all');
  const [range, setRange] = useState<DateRange>({ from: null, to: null });
  // The zone the picker shows its range in: the dates are read in it.
  const [tz, setTz] = useState<TimeWindowTz>('utc');
  const now = today();

  const extra = useMemo(() => {
    const e: Record<string, string> = { status: showRevoked ? 'all' : 'active' };
    if (range.from) e.date_from = range.from;
    if (range.to) e.date_to = range.to;
    return e;
  }, [showRevoked, range]);

  const queryFn = async (p: ResourceDataGridParams): Promise<PaginatedResponse<TourSelfregLink>> => {
    const x = p.extra ?? {};
    const r = await toursApi.listSelfregLinks(tourId, {
      kind: 'dated',
      status: x.status as SelfregLinkListParams['status'],
      q: p.search?.trim() || undefined,
      date_from: (x.date_from as string | undefined) ?? undefined,
      date_to: (x.date_to as string | undefined) ?? undefined,
      ordering: (p.ordering as SelfregLinkListParams['ordering']) ?? undefined,
      page: p.page,
      page_size: p.pageSize,
    });
    return { count: r.count, next: null, previous: null, results: r.results };
  };

  const columns = useMemo<GridColDef<TourSelfregLink>[]>(
    () => [
      {
        field: 'date',
        headerName: 'Date',
        width: DATED_COLUMN_WIDTHS.date,
        filterable: false,
        renderCell: (p) => {
          const date = p.row.date;
          if (!date) return '—';
          const past = date < now;
          const revoked = p.row.revoked_ts;
          return (
            <Tooltip
              title={revoked ? `${formatWallDate(date)} · revoked ${formatFullDate(revoked)}` : formatWallDate(date)}
              describeChild
            >
              <Box sx={{ display: 'grid', minWidth: 0 }}>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    lineHeight: revoked ? 1.15 : undefined,
                    color: past || revoked ? 'text.secondary' : 'text.primary',
                    // A revoked link reads as the tour-wide list's revoked rows: struck, and in words.
                    textDecoration: revoked ? 'line-through' : 'none',
                  }}
                  data-past={past || undefined}
                >
                  {past && <HistoryIcon aria-hidden sx={{ fontSize: 14 }} />}
                  {shortWallDate(date)}
                  {past && (
                    <Box component="span" sx={VISUALLY_HIDDEN}>
                      , past
                    </Box>
                  )}
                </Box>
                {revoked && (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ lineHeight: 1.2 }} data-revoked-word>
                    Revoked {formatShortDate(revoked)}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: 'kind',
        headerName: 'Kind',
        description: 'Guide: a guide booking’s own link. Custom: a link made by hand.',
        width: DATED_COLUMN_WIDTHS.kind,
        sortable: false,
        filterable: false,
        valueGetter: (_v, row) => KIND_LABEL[row.kind],
        renderCell: (p) => {
          const Icon = KIND_ICON[p.row.kind];
          return (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <Icon aria-hidden sx={{ fontSize: 16, color: 'text.secondary' }} />
              {KIND_LABEL[p.row.kind]}
            </Box>
          );
        },
      },
      {
        field: 'tags',
        headerName: 'Tags',
        description: 'The tags a registration through the link receives (departure time, guide)',
        flex: 1,
        minWidth: DATED_COLUMN_WIDTHS.tags,
        sortable: false,
        filterable: false,
        valueGetter: (_v, row) => row.tags.map((t) => t.name ?? `#${t.id}`).join(', '),
        renderCell: (p) => {
          if (p.row.tags.length === 0) {
            return (
              <Typography variant="body2" color="text.secondary" component="span">
                None
              </Typography>
            );
          }
          const tags = p.row.tags.map((t) => (t.category ? `${t.name ?? t.id} (${t.category})` : (t.name ?? `#${t.id}`)));
          return (
            <Tooltip title={tags.join(', ')} describeChild>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden', minWidth: 0 }}>
                {p.row.tags.map((t) => (
                  <Chip key={t.id} size="small" variant="outlined" label={t.name ?? `#${t.id}`} sx={{ height: 22, flex: 'none' }} />
                ))}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: 'guide_booking',
        headerName: 'Guide booking',
        description: 'The guide booking whose guide link this is',
        flex: 1,
        minWidth: DATED_COLUMN_WIDTHS.guide_booking,
        sortable: false,
        filterable: false,
        valueGetter: (_v, row) => row.guide_booking?.name ?? (row.guide_booking ? `#${row.guide_booking.id}` : ''),
        renderCell: (p) => {
          const b = p.row.guide_booking;
          if (!b) return '—';
          const name = b.name ?? `Booking #${b.id}`;
          return (
            <Clipped tooltip={`Guide booking: ${name}`}>
              {canSeeBookings ? (
                <RouterLink to={`/bookings/${b.id}`} tabIndex={p.tabIndex} style={{ color: 'inherit' }}>
                  {name}
                </RouterLink>
              ) : (
                name
              )}
            </Clipped>
          );
        },
      },
      {
        field: 'created_ts',
        headerName: 'Created',
        width: DATED_COLUMN_WIDTHS.created_ts,
        filterable: false,
        renderCell: (p) => {
          const c = createdWords(p.row.created_ts);
          return <Clipped tooltip={c.tooltip}>{c.text}</Clipped>;
        },
      },
      {
        field: 'registered_count',
        headerName: 'Registered',
        description: 'Bookings registered through the link',
        width: DATED_COLUMN_WIDTHS.registered_count,
        sortable: false,
        filterable: false,
        valueGetter: (_v, row) => registeredShort(row.registered_count),
      },
      {
        field: 'verification',
        headerName: 'Verification',
        description: 'What the guest page verifies, and whether the link follows the tour',
        width: DATED_COLUMN_WIDTHS.verification,
        sortable: false,
        filterable: false,
        valueGetter: (_v, row) => {
          const v = verificationWords(row);
          return `${v.text} · ${v.source}`;
        },
        renderCell: (p) => {
          const v = verificationWords(p.row);
          return <Clipped tooltip={`${v.text} · ${v.source}`}>{v.text}</Clipped>;
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: DATED_COLUMN_WIDTHS.status,
        sortable: false,
        filterable: false,
        valueGetter: (_v, row) => (row.revoked_ts ? `Revoked ${formatShortDate(row.revoked_ts)}` : 'Active'),
        renderCell: (p) =>
          p.row.revoked_ts ? (
            <Clipped tooltip={`Revoked ${formatFullDate(p.row.revoked_ts)}`} sx={{ color: 'text.secondary' }}>
              <RemoveCircleOutlineIcon aria-hidden sx={{ fontSize: 14, verticalAlign: '-2px', mr: 0.5 }} />
              Revoked {formatShortDate(p.row.revoked_ts)}
            </Clipped>
          ) : (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <CheckCircleOutlineIcon aria-hidden sx={{ fontSize: 14 }} />
              Active
            </Box>
          ),
      },
      {
        field: 'link',
        headerName: 'Address',
        width: DATED_COLUMN_WIDTHS.link,
        sortable: false,
        filterable: false,
        valueGetter: (_v, row) => row.link,
        renderCell: (p) => (
          <Clipped
            tooltip={p.row.link}
            sx={{
              fontFamily: MONO,
              fontSize: '0.8125rem',
              textDecoration: p.row.revoked_ts ? 'line-through' : 'none',
              color: p.row.revoked_ts ? 'text.secondary' : 'text.primary',
            }}
          >
            {addressTail(p.row)}
          </Clipped>
        ),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: DATED_COLUMN_WIDTHS.actions,
        getActions: (p) => {
          const revoked = Boolean(p.row.revoked_ts);
          return [
            <CellAction
              key="copy"
              icon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
              label={`Copy link ${addressTail(p.row)}`}
              disabled={revoked}
              reason={REVOKED_NO_COPY}
              onClick={() => onCopy(p.row)}
            />,
            <CellAction
              key="qr"
              icon={<QrCode2Icon sx={{ fontSize: 18 }} />}
              label={`QR code of link ${addressTail(p.row)}`}
              disabled={revoked}
              reason={REVOKED_NO_COPY}
              onClick={(e: MouseEvent<HTMLButtonElement>) => onQr(p.row, e.currentTarget)}
            />,
            <CellAction
              key="menu"
              icon={<MoreHorizIcon sx={{ fontSize: 18 }} />}
              label={`Actions of link ${addressTail(p.row)}`}
              aria-haspopup="menu"
              onClick={(e: MouseEvent<HTMLButtonElement>) => onMenu(p.row, e.currentTarget)}
            />,
          ];
        },
      },
    ],
    [now, canSeeBookings, onCopy, onQr, onMenu],
  );

  return (
    <Box sx={{ display: 'grid', gap: 1, minWidth: 0 }} data-selfreg-dated>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }} data-dated-filters>
        <Segmented<QuickRange>
          label="Quick date ranges"
          fill={false}
          // None while the picker's own range is active.
          value={quick}
          onChange={(q) => {
            setQuick(q);
            setRange(quickRange(q));
          }}
          options={[
            { value: 'upcoming', label: 'Upcoming' },
            { value: 'past30', label: 'Past 30 days' },
            { value: 'all', label: 'All' },
          ]}
          dataAttr="dated-quick"
        />
        <TimeWindowPicker
          keepRelative={false}
          label="Dates"
          allowOpenEnded
          value={[dateToInstant(range.from, false, tz), dateToInstant(range.to, true, tz)]}
          onTzChange={setTz}
          onChange={([from, to]) => {
            setQuick(from || to ? null : 'all');
            setRange({ from: instantToDate(from, tz), to: instantToDate(to, tz) });
          }}
        />
      </Box>
      <ResourceDataGrid<TourSelfregLink>
        queryKey={datedKey(tourId)}
        queryFn={queryFn}
        columns={columns}
        extra={extra}
        defaultPageSize={DATED_PAGE_SIZE}
        // The endpoint caps page_size at 100.
        pageSizeOptions={[DATED_PAGE_SIZE, 50, 100]}
        resetPageOnQueryChange
        initialState={{ pinnedColumns: { left: [...DATED_PINNED.left], right: [...DATED_PINNED.right] } }}
        getRowClassName={(p) => (p.row.revoked_ts ? REVOKED_ROW_CLASS : '')}
        renderError={(_e, retry) => <LoadError what="the date-specific links" onRetry={retry} />}
        layout="auto"
        rowHeight={36}
        emptyMessage={showRevoked ? 'No date-specific links.' : 'No active date-specific links.'}
        headerToolbar={{
          search: true,
          searchPlaceholder: 'Find a link: paste its URL or UUID',
          hideFilterButton: true,
          exportFileName: `tour-${tourId}-date-specific-links`,
        }}
        sx={{
          '& .MuiDataGrid-cell': { fontVariantNumeric: 'tabular-nums', px: '6px' },
          '& .MuiDataGrid-columnHeader': { px: '6px' },
          // Revoked rows: the tour-wide list's tint (the pinned cells carry their own fill).
          [`& .${REVOKED_ROW_CLASS}, & .${REVOKED_ROW_CLASS} .MuiDataGrid-cell--pinnedLeft, & .${REVOKED_ROW_CLASS} .MuiDataGrid-cell--pinnedRight`]:
            { bgcolor: 'action.hover' },
        }}
      />
    </Box>
  );
}
